import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Download, Loader2, RefreshCcw, Save, Target, Upload } from 'lucide-react'

import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { MeasurementField } from '../components/ui/measurement'
import { useToast } from '../components/ui/toast'
import { useMeasurements } from '../hooks/useMeasurements'
import { FitnessHikeTarget, FitnessHikeTargetPayload, getFitnessHikeTargets, updateFitnessHikeTargets } from '../lib/api/fitnessHikeTargets'
import { getPopularHikes, type PopularHike } from '../lib/api/popularHikes'
import { displayToMetric, formatMeasurement } from '../lib/measurements'
import { buildHeaderIndex, downloadCsv, makeGetValue, normalizeText, parseNumberOrNull, parseCsvText, validateCsvSchema, withCsvSchemaColumns, withCsvSchemaRow } from '../utils/csv'

type EditableTarget = FitnessHikeTargetPayload & {
  id?: string
  updatedAt?: string
  popularHikeName?: string | null
  thumbnailUrl?: string | null
  numberOfDays?: number | null
  distanceKm?: number
  elevationGainMeters?: number
  totalElevationLossMeters?: number | null
}

const LEVELS = [1, 2, 3, 4, 5]
const DEFAULT_NAMES: Record<number, string> = {
  1: 'Couch to Trail - Yosemite Valley Floor Loop',
  2: 'Easy - Appalachian Trail: Shenandoah Shortened Central Section',
  3: 'Moderate - Otter Trail',
  4: 'Hard - W Trek: Torres del Paine',
  5: 'Strenuous - Tour du Mont Blanc: Classic Circuit'
}
const DEFAULT_LABEL_IDS: Record<number, string> = {
  1: 'ID_GOAL_LEVEL_BEGINNER_COUCH_TO_TRAIL',
  2: 'ID_GOAL_LEVEL_EASY',
  3: 'ID_GOAL_LEVEL_MODERATE',
  4: 'ID_GOAL_LEVEL_HARD',
  5: 'ID_GOAL_LEVEL_STRENUOUS'
}

const HIKE_CSV_TYPE = 'FitnessHikeTargets'
const HIKE_CSV_VERSION = 4

type HikeCsvKey = 'Level' | 'Name' | 'PopularHikeId' | 'PackWeightKg' | 'LevelLabelStringId'

const HIKE_CSV_COLUMNS: HikeCsvKey[] = [
  'Level', 'Name', 'PopularHikeId', 'PackWeightKg', 'LevelLabelStringId'
]

const HIKE_HEADER_ALIASES: Record<HikeCsvKey, string[]> = {
  Level: ['level'],
  Name: ['name'],
  PopularHikeId: ['popularhikeid', 'hikeid'],
  PackWeightKg: ['packweightkg', 'packweight', 'pack'],
  LevelLabelStringId: ['levellabelstringid']
}

const HIKE_CSV_FALLBACK: Record<HikeCsvKey, number> = {
  Level: 0, Name: 1, PopularHikeId: 2, PackWeightKg: 3, LevelLabelStringId: 4
}

const buildDefaults = (): EditableTarget[] =>
  LEVELS.map((level) => ({
    level,
    name: DEFAULT_NAMES[level],
    levelLabelStringId: DEFAULT_LABEL_IDS[level],
    popularHikeId: null,
    packWeightKg: level === 1 ? 0 : level === 2 ? 4.5359 : level === 3 ? 8.1647 : 12.247
  }))

const getHikeDistanceKm = (hike: PopularHike | undefined | null) =>
  hike?.totalDistanceKm ?? hike?.itinerary?.reduce((sum, day) => sum + (day.distanceKm ?? 0), 0) ?? 0

const getHikeElevationGainMeters = (hike: PopularHike | undefined | null) =>
  hike?.totalElevationGainMeters ?? hike?.itinerary?.reduce((sum, day) => sum + (day.elevationGainMeters ?? 0), 0) ?? 0

const getHikeDays = (hike: PopularHike | undefined | null) =>
  hike?.tripNumberOfDays ?? hike?.itinerary?.length ?? 0

export const FitnessHikeTargetsPage = () => {
  const { addToast } = useToast()
  const measurements = useMeasurements()
  const [targets, setTargets] = useState<EditableTarget[]>(buildDefaults)
  const [popularHikes, setPopularHikes] = useState<PopularHike[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCsvImporting, setIsCsvImporting] = useState(false)
  const [isCsvExporting, setIsCsvExporting] = useState(false)
  const csvInputRef = useRef<HTMLInputElement | null>(null)

  const popularHikeById = useMemo(
    () => new Map(popularHikes.map((hike) => [hike.id, hike])),
    [popularHikes]
  )

  const mergeTargets = useCallback((incoming: FitnessHikeTarget[]) => {
    const map = new Map<number, EditableTarget>()
    const defaults = buildDefaults()
    incoming.forEach((t) => {
      map.set(t.level, {
        id: t.id,
        level: t.level,
        name: t.name || DEFAULT_NAMES[t.level] || `Hiking Fitness ${t.level}`,
        levelLabelStringId: t.levelLabelStringId ?? '',
        popularHikeId: t.popularHikeId ?? null,
        popularHikeName: t.popularHikeName,
        thumbnailUrl: t.thumbnailUrl,
        numberOfDays: t.numberOfDays,
        distanceKm: t.distanceKm ?? 0,
        elevationGainMeters: t.elevationGainMeters ?? 0,
        totalElevationLossMeters: t.totalElevationLossMeters,
        packWeightKg: t.packWeightKg ?? 0,
        updatedAt: t.updatedAt
      })
    })

    const merged = LEVELS.map((level) => map.get(level) ?? { ...defaults.find((d) => d.level === level)! })
    setTargets(merged)
  }, [])

  const loadTargets = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [targetData, hikeData] = await Promise.all([
        getFitnessHikeTargets(),
        getPopularHikes()
      ])
      setPopularHikes(hikeData)
      mergeTargets(targetData)
    } catch (err) {
      console.error('Failed to load fitness hike targets', err)
      setError('Could not load hike targets')
      addToast({
        title: 'Error loading hikes',
        description: 'Please refresh or try again later.',
        variant: 'error'
      })
    } finally {
      setLoading(false)
    }
  }, [addToast, mergeTargets])

  useEffect(() => {
    void loadTargets()
  }, [loadTargets])

  const handleChange = (level: number, field: 'name' | 'popularHikeId' | 'levelLabelStringId', value: string) => {
    setTargets((prev) =>
      prev.map((target) =>
        target.level === level
          ? {
              ...target,
              [field]: field === 'popularHikeId' ? value || null : value
            }
          : target
      )
    )
  }

  const handleMetricChange = (
    level: number,
    field: 'packWeightKg',
    value: number | null
  ) => {
    setTargets((prev) =>
      prev.map((target) =>
        target.level === level
          ? {
              ...target,
              [field]: Math.max(0, value ?? 0)
            }
          : target
      )
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload: FitnessHikeTargetPayload[] = targets.map((t) => ({
        level: t.level,
        name: t.name?.trim() || DEFAULT_NAMES[t.level] || `Hiking Fitness ${t.level}`,
        levelLabelStringId: t.levelLabelStringId?.trim() ?? '',
        popularHikeId: t.popularHikeId || null,
        packWeightKg: Number.isFinite(t.packWeightKg) ? t.packWeightKg : 0
      }))

      const updated = await updateFitnessHikeTargets(payload)
      mergeTargets(updated)

      addToast({
        title: 'Saved',
        description: 'Fitness proxy hikes updated.'
      })
    } catch (err) {
      console.error('Failed to save fitness hike targets', err)
      setError('Could not save hike targets')
      addToast({
        title: 'Save failed',
        description: 'Please try again.',
        variant: 'error'
      })
    } finally {
      setSaving(false)
    }
  }

  const triggerCsvImport = () => {
    if (csvInputRef.current) {
      csvInputRef.current.value = ''
      csvInputRef.current.click()
    }
  }

  const handleExportCsv = () => {
    setIsCsvExporting(true)
    try {
      const rows = targets.map((t) => [
        t.level.toString(),
        t.name ?? DEFAULT_NAMES[t.level] ?? `Hiking Fitness ${t.level}`,
        t.popularHikeId ?? '',
        t.packWeightKg.toString(),
        t.levelLabelStringId ?? ''
      ])
      const timestamp = new Date().toISOString().slice(0, 10)
      downloadCsv(
        withCsvSchemaColumns(HIKE_CSV_COLUMNS),
        rows.map((row) => withCsvSchemaRow(HIKE_CSV_TYPE, HIKE_CSV_VERSION, row)),
        `hikestrong-fitness-proxy-hikes-${timestamp}.csv`
      )
      addToast({ title: 'CSV exported', description: `Exported ${rows.length} targets.` })
    } catch (err) {
      console.error('Failed to export hike targets CSV', err)
      addToast({ title: 'Export failed', description: 'Could not export the CSV file.' })
    } finally {
      setIsCsvExporting(false)
    }
  }

  const handleImportCsv = async (file: File) => {
    setIsCsvImporting(true)
    try {
      const text = await file.text()
      // Parse the whole document so quoted multi-line fields round-trip.
      const rows = parseCsvText(text)
      if (rows.length === 0) throw new Error('CSV file is empty.')

      const header = rows[0].map((v) => v.trim())
      const hasHeader =
        header.some((c) => normalizeText(c) === 'level') &&
        header.some((c) => normalizeText(c) === 'name')

      const dataRows = hasHeader ? rows.slice(1) : rows
      if (dataRows.length === 0) throw new Error('CSV appears to contain only headers.')

      const schemaResult = validateCsvSchema(header, dataRows, {
        expectedType: HIKE_CSV_TYPE,
        currentVersion: HIKE_CSV_VERSION,
        entityName: 'Fitness hike targets'
      })
      if (schemaResult.warning) {
        addToast({ title: 'Legacy CSV format', description: schemaResult.warning, duration: 9000 })
      }

      const headerIndex = hasHeader ? buildHeaderIndex(header, HIKE_HEADER_ALIASES) : null
      const getValue = makeGetValue(headerIndex, HIKE_CSV_FALLBACK)
      const getExplicitValue = (row: string[], aliases: string[]) => {
        if (!hasHeader) return ''
        const normalizedHeader = header.map(normalizeText)
        const index = aliases
          .map(normalizeText)
          .map((alias) => normalizedHeader.indexOf(alias))
          .find((candidate) => candidate >= 0)
        return index === undefined ? '' : (row[index] ?? '').trim()
      }

      const parsed: FitnessHikeTargetPayload[] = dataRows.map((row, idx) => {
        const lineNumber = hasHeader ? idx + 2 : idx + 1
        const level = parseNumberOrNull(getValue(row, 'Level'))
        if (level === null || !LEVELS.includes(level)) {
          throw new Error(`Row ${lineNumber}: level must be 1, 2, 3, 4, or 5.`)
        }
        const packPounds = parseNumberOrNull(getExplicitValue(row, ['PackWeightPounds', 'PackWeightLbs', 'PackWeightLb']))
        const legacyDistanceMiles = parseNumberOrNull(getExplicitValue(row, ['DistanceMiles', 'DistanceMi']))
        const legacyElevationFeet = parseNumberOrNull(getExplicitValue(row, ['ElevationGainFeet', 'ElevationFeet']))
        const explicitPopularHikeId = getExplicitValue(row, ['PopularHikeId', 'HikeId'])

        const target: FitnessHikeTargetPayload = {
          level,
          name: getValue(row, 'Name') || DEFAULT_NAMES[level] || `Hiking Fitness ${level}`,
          popularHikeId: explicitPopularHikeId || null,
          packWeightKg: packPounds === null ? parseNumberOrNull(getValue(row, 'PackWeightKg')) ?? 0 : displayToMetric(packPounds, 'weight', 'Imperial')
        }
        if (header.some(column => normalizeText(column) === 'levellabelstringid')) {
          target.levelLabelStringId = getExplicitValue(row, ['LevelLabelStringId'])
        }
        if (legacyDistanceMiles !== null) {
          target.distanceKm = displayToMetric(legacyDistanceMiles, 'distance', 'Imperial')
        }
        if (legacyElevationFeet !== null) {
          target.elevationGainMeters = displayToMetric(legacyElevationFeet, 'elevation', 'Imperial')
        }
        return target
      })

      const updated = await updateFitnessHikeTargets(parsed)
      mergeTargets(updated)

      addToast({
        title: 'Import complete',
        description: `Imported ${parsed.length} target(s).`
      })
    } catch (err) {
      console.error('Failed to import hike targets CSV', err)
      addToast({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Could not parse or import the CSV file.'
      })
    } finally {
      setIsCsvImporting(false)
    }
  }

  const hasUpdates = useMemo(() => targets.some((t) => t.updatedAt), [targets])

  return (
    <div className="space-y-4">
      <header className="text-center">
        <p className="text-sm uppercase tracking-wide text-muted-foreground">Training Defaults</p>
        <h1 className="text-3xl font-semibold">Fitness Proxy Hikes</h1>
        <p className="text-sm text-muted-foreground">
          Set the label, Popular Hike, and pack weight for all five general plans, from Couch to Trail to Strenuous. Distance and elevation come from the hike itinerary.
        </p>
      </header>

      <Card className="mx-auto max-w-5xl">
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Proxy hikes
            </CardTitle>
            <CardDescription>
              Pack weights display in {measurements.unit === 'Imperial' ? 'imperial' : 'metric'} units and save as metric API values.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0]
                if (file) void handleImportCsv(file)
              }}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={triggerCsvImport}
              disabled={isCsvImporting || loading || saving}
              className="gap-2"
            >
              {isCsvImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              Import CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExportCsv}
              disabled={isCsvExporting || loading}
              className="gap-2"
            >
              {isCsvExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => void loadTargets()} disabled={loading || saving}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Refresh
            </Button>
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save targets
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading targets…
            </div>
          ) : (
            <div className="grid gap-4">
              {targets.map((target) => {
                const selectedHike = target.popularHikeId ? popularHikeById.get(target.popularHikeId) : null
                const distanceKm = selectedHike ? getHikeDistanceKm(selectedHike) : target.distanceKm ?? 0
                const elevationGainMeters = selectedHike ? getHikeElevationGainMeters(selectedHike) : target.elevationGainMeters ?? 0
                const days = selectedHike ? getHikeDays(selectedHike) : target.numberOfDays ?? 0

                return (
                  <div key={target.level} className="rounded-lg border border-muted/60 bg-muted/10 p-4 shadow-sm">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">Level {target.level}</p>
                        <p className="text-xs text-muted-foreground">
                          {distanceKm > 0 || elevationGainMeters > 0
                            ? `${days || 'Multi'} day · ${formatMeasurement(distanceKm, 'distance', measurements.unit)} · ${formatMeasurement(elevationGainMeters, 'elevation', measurements.unit)}`
                            : 'Select a Popular Hike to supply the itinerary.'}
                        </p>
                      </div>
                      {target.updatedAt && (
                        <p className="text-xs text-muted-foreground">
                          Updated {new Date(target.updatedAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="grid gap-3 md:grid-cols-[1fr_1.5fr_1fr]">
                      <div className="space-y-1">
                        <label htmlFor={`name-${target.level}`} className="text-sm font-medium text-foreground">
                          Label
                        </label>
                        <Input
                          id={`name-${target.level}`}
                          value={target.name ?? ''}
                          onChange={(e) => handleChange(target.level, 'name', e.target.value)}
                          placeholder={DEFAULT_NAMES[target.level]}
                        />
                        <label htmlFor={`label-id-${target.level}`} className="text-sm font-medium text-foreground">
                          Level label string ID
                        </label>
                        <Input
                          id={`label-id-${target.level}`}
                          value={target.levelLabelStringId ?? ''}
                          onChange={(e) => handleChange(target.level, 'levelLabelStringId', e.target.value)}
                          placeholder={DEFAULT_LABEL_IDS[target.level]}
                        />
                        <p className="text-xs text-muted-foreground">
                          Edit this ID in String Tables to rename the level in the app. Leave blank to use the Label above.
                        </p>
                      </div>
                      <div className="space-y-1">
                        <label htmlFor={`popular-hike-${target.level}`} className="text-sm font-medium text-foreground">
                          Popular Hike
                        </label>
                        <select
                          id={`popular-hike-${target.level}`}
                          value={target.popularHikeId ?? ''}
                          onChange={(e) => handleChange(target.level, 'popularHikeId', e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">Select a Popular Hike</option>
                          {popularHikes.map((hike) => (
                            <option key={hike.id} value={hike.id}>
                              {hike.tripName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <MeasurementField
                        id={`pack-${target.level}`}
                        label="Pack weight"
                        kind="weight"
                        min={0}
                        valueMetric={target.packWeightKg}
                        emptyValue={0}
                        onValueMetricChange={(value) => handleMetricChange(target.level, 'packWeightKg', value)}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="text-xs text-muted-foreground">
            {hasUpdates
              ? 'Changes are saved for Base Camp and consumed by the mobile onboarding flow.'
              : 'No saved targets yet — choose hikes and save to set the defaults.'}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
